import { Stack, Box, Icons, NoData } from '@cast/design-system';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { useInventoryWorkloadsMetrics } from '../_hooks/useInventoryWorkloadsMetrics';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import { WorkloadsSummaryMetricsChartDataKey } from './_components/_types';

import { ResourceChart } from '../../../_components/ui/ResourceChart';

import { ResourceAvgMetrics } from './_components/ResourceAvgMetrics';
import { inventoryWorkloadsChartZoomProps } from '../../../_hooks/useInventoryWorkloadsChartZoomProps';
import { chartDataSettings } from './_components/_constants';

export const CpuUtilizationChart = () => {
  const { timeZone, getRange } = useInventoryWorkloadsTimeRangeSelection();
  const { from, to } = getRange();
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<WorkloadsSummaryMetricsChartDataKey>();

  const {
    dataOrFixture: summaryMetrics,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInventoryWorkloadsMetrics();

  const hasError = !!error;
  const isChartLoading = isLoading || (hasError && isFetching);
  const cpuData = summaryMetrics?.cpu;
  const showEmptyState =
    !isChartLoading && !hasError && (cpuData?.length ?? 0) === 0;

  return (
    <WidgetLayout title="CPU utilization" testId="cpu-utilization-chart">
      {hasError && !isFetching ? (
        <FailedToLoad
          title="Failed to load CPU utilization"
          refresh={refetch}
        />
      ) : showEmptyState ? (
        <NoData
          title="No data found"
          body="Try selecting a different time period."
          icon={<Icons.Question weight="fill" />}
          testId="cpu-utilization-chart-empty"
        />
      ) : (
        <Stack gap={12} height="100%">
          <ResourceAvgMetrics
            data={cpuData}
            unit="CPU"
            toggleMetric={toggleMetric}
            isMetricDisabled={isMetricDisabled}
            isLoading={isChartLoading}
            testId="cpu-utilization-chart-avg-metrics"
          />
          <Box flex={1} display="flex">
            <ResourceChart
              data={cpuData ?? []}
              isLoading={isChartLoading}
              unit="CPU"
              isMetricDisabled={(key) =>
                isMetricDisabled(key as WorkloadsSummaryMetricsChartDataKey)
              }
              timeZone={timeZone}
              chartDataSettings={chartDataSettings}
              dateRange={{ from, to }}
              timeUnit="hour"
              filler={() => ({})}
              zoomProps={inventoryWorkloadsChartZoomProps}
            />
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
